/**
 * The Ledger — the screen at the end of a sitting.
 *
 * ============================== WHAT IT REPLACES ===============================
 * `Play.tsx`'s reveal panel, which was an overlay laid over the felt: a table still
 * visible behind the one screen that is supposed to be the end of it. This is a SCREEN.
 * The felt is not drawn behind it, there is no way back to a table that has already
 * been settled, and the two ways on are the two a player actually wants — the same seat
 * again, or the saloon.
 *
 * ================================ WHAT IS ON IT ================================
 *   - The net, in Bucks, big. It is the number the player came for and it is the only
 *     thing on the screen set at that size.
 *   - One row of what happened: hands, bb/100, the biggest pot, the EV given up — and
 *     for a tournament the place and the prize, and for blackjack accuracy and EV in
 *     place of the two that do not apply to it. One component, a row per kind.
 *   - THE REVEAL, as a cast list with faces. Every word of it comes from
 *     `Game.reveal()`: the name the player saw, the archetype they were, the profile's
 *     own blurb, and `chartAdherence`, which is the one figure about a bot that can be
 *     verified by counting. Nothing here is written by this screen. If the engine has
 *     no line for a seat, the seat has no line.
 *   - ONE drill, chosen from what this sitting shows, per §7. It is absent when there
 *     is nothing true to say, which is most short sittings.
 * ===============================================================================
 */
import { Link } from "react-router-dom";
import { seatAvatar } from "../art/casting";
import { BucksIcon } from "../lib/mark";
import { LEDGER_COPY, nextDrill, type SittingSummary } from "./copy";
import "./rooms.css";

/** One revealed seat, exactly as `Game.reveal()` produces it, plus the picture. */
export interface LedgerCastRow {
  seat: number;
  name: string;
  archetype: string;
  blurb: string;
  chartAdherence: number;
}

export interface LedgerProps {
  summary: SittingSummary;
  /** The card's name, so the screen says which table this was. */
  title: string;
  /**
   * What the sitting did to the wallet, in Bucks — three states, not two.
   *
   *   null                     the sitting was never funded: a drill-shaped seat, or a
   *                            wallet that could not be reached when it started.
   *   { paid, credited: null } it WAS funded and has not settled: the post failed, and
   *                            leaving the table again will retry it.
   *   { paid, credited }       settled. The net is the difference, and it is the same
   *                            pair of numbers the header's gauge moved by.
   *
   * The middle one is why this is an object rather than a signed number. With a plain
   * `netBucks: number | null`, a sitting that had taken two hundred Bucks and failed to
   * settle printed "No Bucks changed hands", which is the one thing on this screen that
   * must never be wrong.
   */
  money: { paid: number; credited: number | null } | null;
  /**
   * The settlement is in flight.
   *
   * `endSitting` opens this screen and posts the settlement in the same breath, so for
   * the first round trip `money.credited` is null for a perfectly healthy sitting. That
   * is a different sentence from "it failed and will retry", and printing either of
   * them as a zero would be the worst of the three.
   */
  settling?: boolean;
  /** The table's own money formatter, for chip amounts in the row. */
  fmt: (v: number) => string;
  cast: readonly LedgerCastRow[];
  /**
   * Phase 6, box 0(3): seat index -> roster slug for the sitting being reported.
   *
   * The reveal names the archetype, and the portrait beside it has to be the portrait
   * that was on the felt — otherwise the one screen whose job is to say "this is who
   * you were playing" shows a face the player never saw. Omitted, every row falls back
   * to the roster's own face for its name.
   */
  faces?: Record<number, string>;
  /** Same card, new receipt — through the seat card again. */
  onAgain: () => void;
  onSaloon: () => void;
  /** Whatever the save produced, verbatim. Never invented here. */
  note?: string | null;
  /**
   * PHASE 9 (blackjack): the sitting's seed, revealed now that the sitting is over.
   *
   * Poker's pattern — the commitment on the seat card before the first card, the seed
   * after the last. The blackjack room printed its seed under the felt for the whole
   * sitting, and anyone who replayed the shoe from it knew every hole card; with the
   * stepper open that is worth real Bucks. Poker passes nothing here: its seed is in
   * `HandHistory`, under this screen.
   */
  proof?: { commitment: string; seed: string; verifyHref: string } | null;
}

const num = (v: number) => v.toLocaleString("en-US");
const place = (n: number) => {
  const t = n % 10, h = n % 100;
  if (t === 1 && h !== 11) return `${n}st`;
  if (t === 2 && h !== 12) return `${n}nd`;
  if (t === 3 && h !== 13) return `${n}rd`;
  return `${n}th`;
};

export function Ledger(props: LedgerProps) {
  const { summary: s, title, money, settling = false, fmt, cast, faces, onAgain, onSaloon, note, proof } = props;
  const drill = nextDrill(s);
  const settled = money && money.credited !== null;
  const netBucks = settled ? money.credited! - money.paid : null;
  const up = (netBucks ?? 0) > 0, down = (netBucks ?? 0) < 0;

  return (
    <section className="ledger">
      <div className="ledger-body">
        <p className="ledger-kicker">{LEDGER_COPY.title} · {title}</p>

        {/* The net, in Bucks, big. A sitting that touched no wallet says nothing here
            rather than printing a zero it cannot stand behind. */}
        {netBucks === null ? (
          <p className="ledger-net ledger-net-flat">
            <span className="ledger-net-word">
              {!money ? LEDGER_COPY.unfunded
                : settling ? LEDGER_COPY.settling
                : LEDGER_COPY.unsettled}
            </span>
          </p>
        ) : (
          <p className={"ledger-net" + (up ? " ledger-net-up" : down ? " ledger-net-down" : " ledger-net-flat")}>
            <span className="ledger-net-word">
              {up ? LEDGER_COPY.netUp : down ? LEDGER_COPY.netDown : LEDGER_COPY.netFlat}
            </span>
            <span className="ledger-net-n num">
              <BucksIcon />
              {up ? "+" : down ? "−" : ""}{num(Math.abs(netBucks))}
            </span>
          </p>
        )}

        {s.hands <= 0 ? (
          /* And only claim the buy-in came back once it actually has. */
          <p className="ledger-none muted">
            {money && money.credited === null ? LEDGER_COPY.noHandsYet : LEDGER_COPY.noHands}
          </p>
        ) : (
          <Row s={s} fmt={fmt} />
        )}

        {/* Tournaments: the place, and the prize the pool actually paid. */}
        {s.kind === "poker-tournament" && s.placement && (
          <p className="ledger-place">
            {LEDGER_COPY.place} <b>{place(s.placement.place)}</b> of {s.placement.of}
            {s.prize !== null && s.prize !== undefined && (
              <> · {LEDGER_COPY.prize} <b className="num"><BucksIcon />{num(s.prize)}</b></>
            )}
          </p>
        )}

        {/* §7: one line, one tap, and only when this sitting gave it something to say. */}
        {drill && (
          <aside className="ledger-drill">
            <p><b>{LEDGER_COPY.drillTitle}.</b> {drill.because}</p>
            <Link className="btn-small" to={drill.href}>{drill.title}</Link>
          </aside>
        )}

        <div className="ledger-go">
          <button className="btn" type="button" onClick={onAgain}>{LEDGER_COPY.again}</button>
          <button className="btn-quiet" type="button" onClick={onSaloon}>{LEDGER_COPY.saloon}</button>
        </div>

        {note && <p className="muted small ledger-note" role="status">{note}</p>}

        {proof && (
          <section className="ledger-proof" data-td="proof">
            <h2>{LEDGER_COPY.proofTitle}</h2>
            <p className="muted small">{LEDGER_COPY.proofLine}</p>
            <dl>
              <dt>{LEDGER_COPY.proofCommitment}</dt>
              <dd><code className="seedcode">{proof.commitment}</code></dd>
              <dt>{LEDGER_COPY.proofSeed}</dt>
              <dd><code className="seedcode">{proof.seed}</code></dd>
            </dl>
            <Link className="btn-small" to={proof.verifyHref}>{LEDGER_COPY.proofCheck}</Link>
          </section>
        )}

        {cast.length > 0 && (
          <section className="ledger-cast">
            <h2>{LEDGER_COPY.castTitle}</h2>
            <p className="muted small">{LEDGER_COPY.castNote}</p>
            <ul>
              {cast.map((c) => {
                /* Box 0(3): the face this seat actually wore at this table. */
                const src = seatAvatar(faces, c.seat, c.name, "neutral");
                return (
                  <li key={c.seat}>
                    {src
                      ? <img src={src} alt="" aria-hidden="true" />
                      : <span className="ledger-blank" aria-hidden="true" />}
                    <div>
                      <b>{c.name}</b>
                      <span className="ledger-arch">{c.archetype}</span>
                      {/* The profile's own words. This screen never writes one. */}
                      <p className="muted small">{c.blurb}</p>
                    </div>
                    <span className="ledger-adh num"
                          title="How often this seat played the solved chart">
                      {Math.round(c.chartAdherence * 100)}%
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="muted small">{LEDGER_COPY.adherenceNote}</p>
          </section>
        )}
      </div>
    </section>
  );
}

/**
 * The row, per kind.
 *
 * Poker counts hands, bb/100, the biggest pot and the EV given up. Blackjack counts
 * hands, how often the player took the exact-EV action, and what the rest cost — the
 * same shape saying the two things that room actually measures. A cell whose number
 * does not exist is drawn as an em dash rather than a zero, because a zero here is a
 * claim and a dash is the truth.
 */
function Row({ s, fmt }: { s: SittingSummary; fmt: (v: number) => string }) {
  const cells: { label: string; value: string }[] = s.kind === "blackjack"
    ? [
        { label: LEDGER_COPY.hands, value: num(s.hands) },
        { label: LEDGER_COPY.accuracy, value: s.accuracy === null || s.accuracy === undefined ? "—" : `${Math.round(s.accuracy * 100)}%` },
        { label: LEDGER_COPY.evGivenUp, value: s.exactLocked ? LEDGER_COPY.evLocked
            : s.evGivenUp === null || s.evGivenUp === undefined ? "—" : fmt(s.evGivenUp) },
      ]
    : [
        { label: LEDGER_COPY.hands, value: num(s.hands) },
        { label: LEDGER_COPY.bb100, value: s.bb100 === null ? "—" : s.bb100.toFixed(1) },
        { label: LEDGER_COPY.biggestPot, value: s.biggestPot === null || s.biggestPot === undefined ? "—" : fmt(s.biggestPot) },
        { label: LEDGER_COPY.evLost, value: fmt(s.evLost) },
      ];
  return (
    <dl className="ledger-row">
      {cells.map((c) => (
        <div key={c.label}>
          <dt>{c.label}</dt>
          <dd className="num">{c.value}</dd>
        </div>
      ))}
    </dl>
  );
}
